import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IgntrIconComponent, IconName } from 'app/shared/igntr';
import { DashboardCardComponent } from './dashboard-card.component';
import { GamificationService } from 'app/api/services/gamification.service';
import {
  GamificationProfileDto,
  UserAchievementViewDto,
  UserChallengeViewDto,
} from 'app/api/models/gamification/gamification.dtos';

interface Badge {
  id: string;
  name: string;
  earned: boolean;
  icon: IconName;
  tone: string;
}

interface Goal {
  label: string;
  done: boolean;
}

// Wired to the live gamification API (profile / achievements / challenges).
@Component({
  selector: 'app-dashboard-gamification',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoRootModule,
    IgntrIconComponent,
    DashboardCardComponent,
  ],
  template: `
    <app-dashboard-card
      [title]="'dashGamificationTitle' | transloco"
      [sub]="'dashGamificationLevel' | transloco: { level: level }"
    >
      <div class="grid grid-cols-3 gap-2 mb-4">
        <div
          class="rounded-xl border border-amber-200/60 bg-gradient-to-br from-amber-50 to-orange-50 p-3 text-center"
        >
          <div class="text-amber-600 mb-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/70">
            <igntr-icon name="fire" [size]="16"></igntr-icon>
          </div>
          <div class="text-[20px] font-semibold tabular-nums text-amber-900">{{ streak }}</div>
          <div class="text-[10px] uppercase tracking-[0.1em] text-amber-700/80 font-medium">
            {{ 'dashStatStreak' | transloco }}
          </div>
        </div>

        <div
          class="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 text-center"
        >
          <div class="text-accent-deep mb-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white">
            <igntr-icon name="zap" [size]="16"></igntr-icon>
          </div>
          <div class="text-[20px] font-semibold tabular-nums text-slate-900">{{ xpFormatted }}</div>
          <div class="text-[10px] uppercase tracking-[0.1em] text-slate-500 font-medium">
            {{ 'dashStatXp' | transloco }}
          </div>
        </div>

        <div
          class="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 text-center"
        >
          <div class="text-amber-600 mb-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white">
            <igntr-icon name="star" [size]="16"></igntr-icon>
          </div>
          <div class="text-[20px] font-semibold tabular-nums text-slate-900">{{ badgesEarned }}</div>
          <div class="text-[10px] uppercase tracking-[0.1em] text-slate-500 font-medium">
            {{ 'dashStatBadges' | transloco }}
          </div>
        </div>
      </div>

      @if (profile?.nextLevel != null) {
        <div class="mb-4">
          <div class="flex items-baseline justify-between mb-1.5">
            <span class="text-[11.5px] text-slate-600">
              {{ 'dashGamificationLevelProgress' | transloco: { from: level, to: profile?.nextLevel } }}
            </span>
            <span class="text-[11px] text-slate-400 font-mono tabular-nums">
              {{ xpFormatted }} / {{ nextLevelFormatted }} XP
            </span>
          </div>
          <div class="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-accent to-brand rounded-full transition-all"
              [style.width.%]="pct"
            ></div>
          </div>
        </div>
      }

      @if (goals.length) {
        <div class="rounded-xl border border-accent/20 bg-accent/5 p-3 mb-4">
          <div class="flex items-center gap-2 mb-2">
            <span class="text-accent-deep"><igntr-icon name="flag" [size]="14"></igntr-icon></span>
            <span class="text-[12px] font-semibold text-accent-deep">
              {{ 'dashWeeklyGoals' | transloco }}
            </span>
            <span class="ml-auto text-[10.5px] text-slate-500 font-mono">{{ goalsDone }} / {{ goals.length }}</span>
          </div>
          <div class="space-y-[6px]">
            @for (g of goals; track $index) {
              <div
                [class]="
                  'flex items-center gap-2 text-[11.5px] ' +
                  (g.done ? 'text-slate-500 line-through' : 'text-slate-700')
                "
              >
                <span
                  [class]="
                    'w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ' +
                    (g.done
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white border border-slate-300')
                  "
                >
                  @if (g.done) {
                    <span class="scale-[0.55]"><igntr-icon name="check" [size]="14"></igntr-icon></span>
                  }
                </span>
                {{ g.label }}
              </div>
            }
          </div>
        </div>
      }

      @if (badges.length) {
        <div>
          <div class="flex items-baseline justify-between mb-2">
            <span class="text-[11.5px] uppercase tracking-[0.12em] text-slate-400 font-medium">
              {{ 'dashBadgesTitle' | transloco }}
            </span>
            <a
              routerLink="/gamification"
              class="text-[11px] text-slate-500 hover:text-slate-900"
            >
              {{ 'dashBadgesViewAll' | transloco: { count: totalBadges } }}
            </a>
          </div>
          <div class="grid grid-cols-4 gap-2">
            @for (b of badges; track b.id) {
              <div class="text-center" [title]="b.name">
                <div
                  [class]="
                    'mx-auto w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm ' +
                    (b.earned
                      ? 'bg-gradient-to-br ' + b.tone
                      : 'bg-slate-100 text-slate-300 grayscale opacity-60')
                  "
                >
                  <igntr-icon [name]="b.icon" [size]="20"></igntr-icon>
                </div>
                <div
                  [class]="
                    'mt-1 text-[10px] leading-tight truncate ' +
                    (b.earned ? 'text-slate-700' : 'text-slate-400')
                  "
                >
                  {{ b.name }}
                </div>
              </div>
            }
          </div>
        </div>
      }
    </app-dashboard-card>
  `,
})
export class DashboardGamificationComponent implements OnInit {
  private static readonly TONES = [
    'from-emerald-500 to-teal-600',
    'from-rose-500 to-red-600',
    'from-violet-500 to-purple-600',
    'from-amber-500 to-orange-600',
    'from-sky-500 to-blue-600',
    'from-fuchsia-500 to-pink-600',
    'from-lime-500 to-green-600',
    'from-cyan-500 to-teal-600',
  ];

  profile?: GamificationProfileDto;
  goals: Goal[] = [];
  badges: Badge[] = [];
  totalBadges = 0;

  constructor(private gamification: GamificationService) {}

  ngOnInit(): void {
    this.gamification.getMyProfile().subscribe((p) => (this.profile = p));

    this.gamification.getMyAchievements().subscribe((a) => {
      this.totalBadges = a.length;
      // Show earned first, then locked, capped to the 8-slot grid.
      const sorted = [...a].sort(
        (x, y) => Number(y.unlocked) - Number(x.unlocked),
      );
      this.badges = sorted.slice(0, 8).map((ach, i) => this.toBadge(ach, i));
    });

    this.gamification.getMyChallenges().subscribe((c) => {
      this.goals = c.slice(0, 6).map((ch: UserChallengeViewDto) => ({
        label: ch.name,
        done: ch.completed,
      }));
    });
  }

  get streak(): number {
    return this.profile?.streakDays ?? 0;
  }

  get xp(): number {
    return this.profile?.totalPoints ?? 0;
  }

  get level(): number {
    return this.profile?.currentLevel ?? 0;
  }

  get badgesEarned(): number {
    return this.badges.filter((b) => b.earned).length;
  }

  get goalsDone(): number {
    return this.goals.filter((g) => g.done).length;
  }

  get pct(): number {
    return this.profile?.levelProgressPercent ?? 0;
  }

  get xpFormatted(): string {
    return this.xp.toLocaleString();
  }

  get nextLevelFormatted(): string {
    return (this.profile?.nextLevelMinPoints ?? this.xp).toLocaleString();
  }

  private toBadge(a: UserAchievementViewDto, index: number): Badge {
    return {
      id: a.achievementId,
      name: a.name,
      earned: a.unlocked,
      icon: a.unlocked ? 'award' : 'lock',
      tone: DashboardGamificationComponent.TONES[
        index % DashboardGamificationComponent.TONES.length
      ],
    };
  }
}
